/**
 * Shipping-methods CRUD builders — the boundary between the seller-facing
 * shipping editors (`components/my-listings/EditShippingTab.tsx`,
 * `components/shipping-settings/shipping-settings.tsx`) and the new
 * `/api/listings/shipping-methods` routes (B4 + B5).
 *
 *   components/my-listings/EditShippingTab.tsx              ┐
 *   components/shipping-settings/shipping-settings.tsx      ┴── intent ──▶ addShippingMethod
 *                                                                                 │
 *                                                                                 ├── safeParse (boundary)
 *                                                                                 └── POST ─── 2xx ──▶ { id }
 *                                                                                            │
 *                                                                                            └── !ok ──▶ throws ShippingMethodError
 *
 * Why this exists:
 *  - Pre-extract both producers did `supabase.from('shipping_methods').{insert,
 *    update,delete,select}` directly from React — a Silent NACK class violation
 *    (CLAUDE.md HARD GATE). Block B introduces the HTTP boundary; Block C
 *    swaps the inline supabase calls for these builders.
 *  - Mirror of `lib/chat/chat-send.ts` and `lib/listings/update-listing.ts`
 *    pattern. Same `seal` ▶ `postJson` seam for write paths; raw `fetch` for
 *    DELETE (no JSON body) and GET (no body at all) — `postJson` accepts only
 *    POST/PUT/PATCH and exists to gate JSON-body egress through `Built<T>`.
 *
 * Design notes:
 *  - `addShippingMethod` returns the inserted id so optimistic callers can
 *    wire it into local state without a re-fetch (current EditShippingTab
 *    pattern). The wire body carries a client-generated uuid; the route
 *    inserts it as-is.
 *  - `updateShippingMethod` mirrors PATCH semantics: any subset of writable
 *    fields. Caller decides what to send; absent keys are untouched server-side.
 *  - `deleteShippingMethod` uses raw `fetch` (no JSON body needed). RLS gates
 *    write access to `auth.uid() = user_id`; the route additionally pins
 *    `WHERE user_id = $auth.uid()` for defence in depth.
 *  - `listShippingMethods` returns the seller's own rows. Buyers read methods
 *    via the listing-detail RPC (joined through `listings.shipping_config`),
 *    NOT through this endpoint.
 *  - `ShippingMethodRow` mirrors the `shipping_methods` columns
 *    (`id, user_id, name, description, price, banned_countries, currency,
 *    created_at`). Source: `supabase/migrations/20251105184538_20251105_baseline.sql:553`
 *    + `20260112212242_baseline_last.sql:194` (currency column).
 *
 * @see app/api/listings/shipping-methods/route.ts — POST + GET (commit B4)
 * @see app/api/listings/shipping-methods/[id]/route.ts — PATCH + DELETE (B5)
 * @see packages/contract/src/listing-shipping-methods.ts — wire schemas
 * @see components/my-listings/EditShippingTab.tsx — producer (Block C swap)
 * @see components/shipping-settings/shipping-settings.tsx — sibling producer
 * @see lib/chat/chat-send.ts — pattern reference (Sibling-E §4 #3c)
 * @see .tondocs/skills/hex.md §2 — Functional Core / Imperative Shell
 * @see .tondocs/todos/listings/p1-edit-listing-l1-l2-l4-hardening.md
 */

import {
  AddShippingMethodRequestSchema,
  UpdateShippingMethodRequestSchema,
  seal,
  type AddShippingMethodRequest,
  type UpdateShippingMethodRequest,
} from "@kraahl/contract"
import { postJson } from "@/lib/api/post-json"

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

/**
 * Thrown when the assembled body fails the contract Zod parse before fetch.
 * Programmer-bug signal. Mirror of `ChatSendContractError`.
 */
export class ShippingMethodContractError extends Error {
  readonly issues: ReadonlyArray<{ path: string; message: string }>
  constructor(
    op: "add" | "update",
    issues: ReadonlyArray<{ path: string; message: string }>,
  ) {
    super(
      `[shipping-methods ${op}] contract parse failed: ${issues
        .map((i) => `${i.path}: ${i.message}`)
        .join("; ")}`,
    )
    this.name = "ShippingMethodContractError"
    this.issues = issues
  }
}

/**
 * Thrown on non-2xx from any shipping-methods endpoint. Mirror of
 * `ChatSendError` — carries `status` + raw `serverError` string so the
 * delivery layer can branch (`ALL_COUNTRIES_BANNED` → inline form error,
 * 404 → toast, etc.).
 */
export class ShippingMethodError extends Error {
  readonly status: number
  readonly serverError: string | null
  constructor(message: string, status: number, serverError: string | null) {
    super(message)
    this.name = "ShippingMethodError"
    this.status = status
    this.serverError = serverError
  }
}

// ---------------------------------------------------------------------------
// Result types
// ---------------------------------------------------------------------------

/**
 * Shape of a `shipping_methods` row as returned by GET. Mirrors the table
 * columns; `created_at` is ISO 8601 from PostgREST.
 */
export interface ShippingMethodRow {
  id: string
  user_id: string
  name: string | null
  description: string | null
  price: string | null
  banned_countries: string[] | null
  currency: string | null
  created_at: string
}

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------

const ENDPOINT = "/api/listings/shipping-methods"

async function readServerError(res: Response): Promise<string | null> {
  const payload = await res.json().catch(() => null)
  if (payload && typeof (payload as { error?: unknown }).error === "string") {
    return (payload as { error: string }).error
  }
  return null
}

// ---------------------------------------------------------------------------
// POST — add
// ---------------------------------------------------------------------------

/**
 * safeParse, POST `/api/listings/shipping-methods`. Returns `{ id }` on
 * success (echoes the client-generated uuid the route inserted).
 */
export async function addShippingMethod(
  intent: AddShippingMethodRequest,
): Promise<{ id: string }> {
  const parsed = AddShippingMethodRequestSchema.safeParse(intent)
  if (!parsed.success) {
    throw new ShippingMethodContractError(
      "add",
      parsed.error.issues.map((i) => ({
        path: i.path.join("."),
        message: i.message,
      })),
    )
  }

  const res = await postJson(ENDPOINT, seal<AddShippingMethodRequest>(intent))
  if (!res.ok) {
    const serverError = await readServerError(res)
    throw new ShippingMethodError(
      serverError ?? `Add shipping method (${res.status})`,
      res.status,
      serverError,
    )
  }

  return (await res.json()) as { id: string }
}

// ---------------------------------------------------------------------------
// PATCH — update
// ---------------------------------------------------------------------------

/**
 * safeParse, PATCH `/api/listings/shipping-methods/${id}`. Returns `{ id }`
 * on success.
 */
export async function updateShippingMethod(
  id: string,
  intent: UpdateShippingMethodRequest,
): Promise<{ id: string }> {
  const parsed = UpdateShippingMethodRequestSchema.safeParse(intent)
  if (!parsed.success) {
    throw new ShippingMethodContractError(
      "update",
      parsed.error.issues.map((i) => ({
        path: i.path.join("."),
        message: i.message,
      })),
    )
  }

  const res = await postJson(
    `${ENDPOINT}/${id}`,
    seal<UpdateShippingMethodRequest>(intent),
    { method: "PATCH" },
  )
  if (!res.ok) {
    const serverError = await readServerError(res)
    throw new ShippingMethodError(
      serverError ?? `Update shipping method (${res.status})`,
      res.status,
      serverError,
    )
  }

  return (await res.json()) as { id: string }
}

// ---------------------------------------------------------------------------
// DELETE
// ---------------------------------------------------------------------------

/**
 * DELETE `/api/listings/shipping-methods/${id}`. Raw fetch — DELETE has no
 * JSON body, so the `postJson` / `Built<T>` seam does not apply. Throws
 * `ShippingMethodError` on non-2xx.
 */
export async function deleteShippingMethod(id: string): Promise<void> {
  const res = await fetch(`${ENDPOINT}/${id}`, { method: "DELETE" })
  if (!res.ok) {
    const serverError = await readServerError(res)
    throw new ShippingMethodError(
      serverError ?? `Delete shipping method (${res.status})`,
      res.status,
      serverError,
    )
  }
}

// ---------------------------------------------------------------------------
// GET — list
// ---------------------------------------------------------------------------

/**
 * GET `/api/listings/shipping-methods`. Returns the caller's own
 * `shipping_methods` rows (route filters `WHERE user_id = $auth.uid()`).
 */
export async function listShippingMethods(): Promise<ShippingMethodRow[]> {
  const res = await fetch(ENDPOINT, { method: "GET" })
  if (!res.ok) {
    const serverError = await readServerError(res)
    throw new ShippingMethodError(
      serverError ?? `List shipping methods (${res.status})`,
      res.status,
      serverError,
    )
  }
  return (await res.json()) as ShippingMethodRow[]
}
